import React, {useEffect, useState} from 'react';
import {ExpressionPath, Expressions, withSystem} from '@packages/systems/wfdl';

import {TEMP_PATH} from '@packages/utilities/expression-path-constants';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  TRecord,
  TText,
  TList,
  TNumber,
  ENumber,
  EText,
} from '@packages/systems/wfdl-ast';

// eslint-disable-next-line webflow/no-dangerous-imports
import PropertyList, {
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {
  CLOSED_STATE,
  OPENED_STATE,
  DeprecatedPopoverBridge,
} from '@packages/systems/design-system/PopoverBridge';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import Textarea from '@packages/systems/design-system/Textarea';
import Button from '@packages/systems/design-system/Button';
import {
  styled,
  styledDiv,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {TransformRotate as RotateIcon} from '@packages/systems/ui/icons';
import {ErrorStateToCopy} from '@packages/systems/users/constants';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const fields = {
  temp: TRecord({
    index: TNumber,
  }),
  users: TRecord({
    type: TText,
  }),
  errors: TList(
    TRecord({
      id: withSystem({private: true}, TText),
      name: withSystem({private: true}, TText),
      copy: withSystem({private: true}, TText),
    })
  ),
} as const;

const ERROR_PATH: ExpressionPath = [{in: 'Record', at: 'errors'}];
const USERS_TYPE_PATH: ExpressionPath = [
  {in: 'Record', at: 'users'},
  {in: 'Record', at: 'type'},
];
const INDEX_PATH: ExpressionPath = [...TEMP_PATH, {in: 'Record', at: 'index'}];

const setIndex = (expr: ContextualExpression, index: number) =>
  Expressions.setIn(stripAll(expr), INDEX_PATH, ENumber(index));

const getName = (item: ContextualExpression): string => {
  const name = ContextualExpressions.getRecord(stripAnn(item))?.name;
  if (!name) return '';
  return ContextualExpressions.getText(stripAnn(name)) || '';
};

const Island = emotionStyled('div')({
  padding: tokens.space[2],
  width: rightSidebarWidth,
});

const getErrorMsgList = (expr: ContextualExpression) => {
  const errorsExpr = ContextualExpressions.getIn(expr, ERROR_PATH);
  if (errorsExpr === undefined) return [];
  return ContextualExpressions.getList(stripAnn(errorsExpr)) || [];
};

const getExprIndex = (expr: ContextualExpression) => {
  const indexExpr = getIn(expr, INDEX_PATH);
  return indexExpr
    ? (ContextualExpressions.getNumber(stripAnn(indexExpr)) ?? 0)
    : 0;
};

const StyledTextArea = styled(Textarea)(({theme}) => ({
  height: theme.gutters.x20 * 5 + theme.gutters.x12,
  resize: 'none',
  padding: theme.gutters.x4,
  fontSize: '12px',
  marginBottom: '8px',
}));

const EditTextHeader = styledDiv(({theme}) => ({
  paddingTop: theme.gutters.x8,
  paddingBottom: theme.gutters.x12 / 2,
  fontFamily: theme.typography.subheading.fontFamily,
  fontSize: theme.typography.subheading.fontSize,
  fontWeight: theme.typography.subheading.fontWeight,
  borderBottomStyle: 'solid',
  borderBottomWidth: theme.control.borderWidth,
  borderBottomColor: theme.colors.backgroundDeepAlt,
}));

const RestoreDefaultButton = ({
  onClick,
  tooltipContent,
}: {
  onClick?: null | (() => void);
  tooltipContent: string;
}) => {
  if (onClick == null) return null;

  return (
    <TooltipTrigger content={tooltipContent} copy alternate placement="bottom">
      <Button
        structure="ghost"
        onClick={onClick}
        compact
        style={{
          width: '100%',
          marginTop: 8,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <RotateIcon
          style={{
            display: 'flex',
            marginRight: 8,
          }}
        />
        Restore default
      </Button>
    </TooltipTrigger>
  );
};

const UserErrorTextArea = ({
  defaultCopy,
  commit,
  initialValue,
}: {
  defaultCopy: string;
  commit: (arg1: string) => void;
  initialValue: string;
}) => {
  const [text, setText] = useState(initialValue);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setText(e.target.value || '');

  useEffect(() => {
    commit(text);
  }, [commit, text]);

  return (
    <>
      <StyledTextArea text={text} onChange={onChange} />
      {text !== defaultCopy && (
        <RestoreDefaultButton
          onClick={() => {
            setText(defaultCopy);
          }}
          tooltipContent={defaultCopy}
        />
      )}
    </>
  );
};

const UserErrorMsgType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function UserErrorMsgRender({expr, dispatch}) {
    const errorMsgs = getErrorMsgList(expr);
    const indexExpr = getExprIndex(expr);
    const userFormTypeExpr = getIn(expr, USERS_TYPE_PATH);
    const userFormType = userFormTypeExpr
      ? (ContextualExpressions.getText(stripAnn(userFormTypeExpr)) ?? '')
      : '';

    const [indexState, setStateIndex] = useState(-1);

    useEffect(() => {
      if (indexState !== -1) {
        dispatch(updateExpressionAction(setIndex(expr, indexState)));
      }
    }, [dispatch, expr, indexState]);

    const renderListItem = (
      listItem: {
        item: ContextualExpression;
        id: string;
      },
      idx: number
    ) => {
      const isSelected = indexExpr === idx;
      const isOpen = indexState === idx;

      const Input = () => {
        const indexCopyPath: ExpressionPath = [
          ...ERROR_PATH,
          {in: 'List', index: idx},
          {in: 'Record', at: 'copy'},
        ];

        const copyExpr = getIn(expr, indexCopyPath);
        const copy = copyExpr
          ? (ContextualExpressions.getText(stripAnn(copyExpr)) ?? '')
          : '';

        const indexIdPath: ExpressionPath = [
          ...ERROR_PATH,
          {in: 'List', index: idx},
          {in: 'Record', at: 'id'},
        ];

        const idExpr = getIn(expr, indexIdPath);
        const id = idExpr
          ? (ContextualExpressions.getText(stripAnn(idExpr)) ?? '')
          : '';

        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"signup-error-state" | "login-error-state" | "update-password-error-state" | "reset-password-error-state" | "account-update-error-state"'.
        const defaultCopy = ErrorStateToCopy(userFormType, id) || copy;

        const setCopy = (textValue: string) => {
          if (defaultCopy === null) return;
          const newExpr = Expressions.setIn(
            stripAll(expr),
            indexCopyPath,
            EText(textValue)
          );
          dispatch(updateExpressionAction(newExpr));
        };

        return (
          <Island>
            <EditTextHeader>Edit text</EditTextHeader>
            {copyExpr && (
              <UserErrorTextArea
                initialValue={copy}
                commit={setCopy}
                defaultCopy={defaultCopy}
              />
            )}
          </Island>
        );
      };
      return (
        <DeprecatedPopoverBridge
          // @ts-expect-error - TS2769 - No overload matches this call.
          key={idx}
          onChange={(popoverState) => {
            if (popoverState.open === false) {
              setStateIndex(-1);
            }
          }}
          state={isOpen ? OPENED_STATE : CLOSED_STATE}
          renderPopoverContent={Input}
          showArrow={false}
          showMask="TRANSPARENT"
          metricsIdentifier="user-error-message-type-popover"
        >
          {({Anchor}) => (
            <Anchor asChild={false}>
              {(ref: React.RefObject<any>) => (
                <PropertyListItem
                  _ref={ref}
                  showIconsByDefault
                  checkedIndicator="radio"
                  isSelected={isSelected}
                  editable
                  id={String(idx)}
                  key={idx}
                  label={getName(listItem.item)}
                  onEditButtonClicked={() => {
                    setStateIndex(idx);
                  }}
                  onClick={() => {
                    dispatch(updateExpressionAction(setIndex(expr, idx)));
                  }}
                />
              )}
            </Anchor>
          )}
        </DeprecatedPopoverBridge>
      );
    };

    return (
      <PropertyList
        disabled={false}
        items={errorMsgs.map((item, id) => ({item, id: String(id)}))}
        renderItem={renderListItem}
      />
    );
  },
});

export default UserErrorMsgType;
